import { AppBuilderOutputOverlay } from '../Nodes/Shared/AppBuilderOutputOverlay';
import { useNodeDataNullable } from './FlowContext';
import type { BaseNodeData } from '@/types/node';
import type { NodeProps } from 'reactflow';
import type { NodeId } from 'web';

type WrappedNodeProps<T extends BaseNodeData> = Omit<NodeProps<T>, 'data'> & {
  data: T;
};

type NodeWrapperProps<T extends BaseNodeData> = Omit<NodeProps<T>, 'data'> & {
  id: NodeId;
  renderNode: (props: WrappedNodeProps<T>) => React.JSX.Element;
};

// provide node data to node components + prevent rendering node when deleted
function NodeWrapper<T extends BaseNodeData>({ id, renderNode, ...otherProps }: NodeWrapperProps<T>) {
  const data = useNodeDataNullable<T>(id);
  if (!data) return null;

  return <AppBuilderOutputOverlay id={id}>{renderNode({ id, ...otherProps, data })}</AppBuilderOutputOverlay>;
}

export function wrapNode<T extends BaseNodeData = BaseNodeData>(
  renderNode: (props: WrappedNodeProps<T>) => React.JSX.Element,
) {
  // eslint-disable-next-line react/display-name
  return (props: NodeProps<T>) => <NodeWrapper {...props} renderNode={renderNode} />;
}
